import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { profileKeys } from "../../profile/profile.utils"
import type {
  ResponseDto,
  UserDto,
  SubmitInviteCodeDto,
  HttpError,
} from "@mimber/schema"

/**
 * Organization-specific hook for submitting an invite code.
 * Links an authenticated user to a waitlist entry post-registration.
 */
export const useSubmitInviteCode = (
  options?: UseMutationOptions<
    ResponseDto<UserDto>,
    HttpError,
    SubmitInviteCodeDto
  >
): UseMutationResult<ResponseDto<UserDto>, HttpError, SubmitInviteCodeDto> => {
  guardAppUsage("mimber-organization", "useSubmitInviteCode")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (dto: SubmitInviteCodeDto) => {
      const response = await server.post<ResponseDto<UserDto>>(
        "account/submit-invite-code",
        dto
      )
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      queryClient.invalidateQueries({ queryKey: profileKeys.getProfile() })
      queryClient.invalidateQueries({ queryKey: ["current-user"] })
    },
  })
}
